import React, { useState } from 'react';
import { mdiPlayCircleOutline } from '@mdi/js';
import { Row, Column, Margin, Button } from '@upgrade/react-components';
import getColumnsCount from '../../../utils/calculate-grid';
import { Section, LargeText } from '../styles';
import Fluid from '../../Fluid';
import InfoContainer from '../InfoContainer';
import { toHtmlSanitize } from '../../../utils/remark-html';
import { StyledBody } from '../typography';
import { StyledVideoButton, StyledVideoButtonText, StyledVideoContainer } from './styles';
import VideoModal from '../VideoModal';
import { Heading } from '../TypographyPublicSite';
import useTrustpilot from '../../../utils/useTrustpilot';
import { InnerContainer } from '../../home/styles';
import TermsLink from '../TermsLink';
import Video from '../Video';

const InfoTypes = ({
  infoTypes: {
    title,
    subtitle,
    text,
    backgroundColor,
    innerColor = 'transparent',
    columns,
    fontColor = 'contentWeak',
    items: itemList,
    descriptionFontWeight,
    alignItems = 'start',
    xsAlignItems,
    showHelpVideo,
    showVideoInModal = true,
    videoTitle,
    videoHeight,
    videoWidth,
    ctaButtonText,
    onCtaButtonClick,
    videoUrl,
    hideTopBorder
  },
  fontWeight,
  subtitleColor,
  titleColor = 'content',
  itemTitleColor = 'content',
  itemTitleSize,
  id,
  sectionStyle,
  imageMaxHeight,
  borderless,
  topBorder,
  constrainSubtitle = false,
  margin,
  disclosuresLinkExperiment: { disclosuresLinkEnabled = false, disclosuresLinkText = 'View Terms' } = {}
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  useTrustpilot();
  const items = itemList ?? [];
  const grid = getColumnsCount(columns || items.length);

  const handleClick = e => {
    e.preventDefault();
    setModalVisible(true);
  };

  const requireInnerLayout = innerColor !== 'transparent';
  const ContentWrapper = requireInnerLayout ? InnerContainer : React.Fragment;
  const contentWrapperProps = requireInnerLayout
    ? { 'data-color': innerColor, verticalPadding: { xs: '2xl', sm: 'lg' } }
    : {};

  return (
    <Section
      data-color={backgroundColor}
      data-font-color={fontColor}
      $margin={margin}
      padding="md"
      style={sectionStyle}
      {...{ id, borderless }}
    >
      <Fluid padding={['md', 'none']}>
        <ContentWrapper {...contentWrapperProps}>
          <Row xsHorizontalAlign="center">
            <Column xs={12} sm={grid.container}>
              <Margin bottom="lg">
                {title && (
                  <Heading as="h2" color={titleColor} marginBottom="sm" size="sm">
                    {title}
                  </Heading>
                )}
                {subtitle && (
                  <Row xsHorizontalAlign="center">
                    <Column xs={12} sm={constrainSubtitle ? 6 : 12}>
                      <StyledBody
                        dangerouslySetInnerHTML={{ __html: toHtmlSanitize(subtitle) }}
                        data-color={subtitleColor}
                      />
                    </Column>
                  </Row>
                )}
                {disclosuresLinkEnabled ? (
                  <Margin top="md">
                    <TermsLink>{disclosuresLinkText}</TermsLink>
                  </Margin>
                ) : null}
              </Margin>
              {text && (
                <div className="section--xl">
                  <LargeText dangerouslySetInnerHTML={{ __html: toHtmlSanitize(text) }} />
                </div>
              )}
              <Row as="ul" className="list--unstyled" xsHorizontalAlign="center">
                {items.map(
                  ({
                    icon,
                    iconColor,
                    iconTitle,
                    image,
                    imageWidth,
                    imageHeight,
                    imagePosition,
                    title: itemTitle,
                    alt,
                    addTitleBorder,
                    description,
                    list,
                    link,
                    ctaTitle,
                    styledText,
                    styledTextAlt,
                    showTrustpilotSection,
                    snippet,
                    xsImagePosition,
                    xsImageHeight,
                    xsImageWidth
                  }) => (
                    <Column as="li" key={itemTitle || description} xs={12} md={grid.colSize}>
                      <InfoContainer
                        {...{
                          icon,
                          iconColor,
                          iconTitle,
                          image,
                          imageWidth,
                          imageHeight,
                          imageMaxHeight,
                          imagePosition,
                          titleColor: itemTitleColor || fontColor,
                          fontColor,
                          titleFontWeight: fontWeight,
                          titleFontSize: itemTitleSize,
                          title: itemTitle,
                          alt,
                          description,
                          list,
                          link,
                          ctaTitle,
                          addTitleBorder,
                          descriptionFontWeight,
                          styledText,
                          styledTextAlt,
                          showTrustpilotSection,
                          topBorder: hideTopBorder ? false : topBorder,
                          alignItems,
                          snippet,
                          xsAlignItems,
                          xsImagePosition,
                          xsImageHeight,
                          xsImageWidth
                        }}
                      />
                    </Column>
                  )
                )}
              </Row>
            </Column>
          </Row>
          {showHelpVideo && (
            <Row xsHorizontalAlign="center">
              {videoTitle && <Heading size="sm">{videoTitle}</Heading>}
              <StyledVideoContainer xs={12}>
                {showVideoInModal ? (
                  <>
                    <StyledVideoButton emphasis="secondary" icon={mdiPlayCircleOutline} onClick={handleClick}>
                      <StyledVideoButtonText>{ctaButtonText}</StyledVideoButtonText>
                    </StyledVideoButton>
                    <VideoModal
                      visible={modalVisible}
                      onClose={() => setModalVisible(false)}
                      padding="1.5rem"
                      maxWidth="90%"
                      videoUrl={videoUrl}
                    />
                  </>
                ) : (
                  <Column xs={12}>
                    <Video title={title} {...{ videoUrl, height: videoHeight, width: videoWidth }} />
                  </Column>
                )}
              </StyledVideoContainer>
            </Row>
          )}
          {onCtaButtonClick ? (
            <Row xsHorizontalAlign="center">
              <Column xs={12} sm={6} lg={4}>
                <Margin top={{ xs: 'lg', sm: '3xl' }}>
                  <Button fullWidth onClick={onCtaButtonClick}>
                    {ctaButtonText}
                  </Button>
                </Margin>
              </Column>
            </Row>
          ) : null}
        </ContentWrapper>
      </Fluid>
    </Section>
  );
};

export default InfoTypes;
